"use client";

import * as Sentry from "@sentry/nextjs";
import {
  createContext,
  ReactNode,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface SentryContextType {
  captureError: (error: Error, context?: Record<string, unknown>) => void;
  addBreadcrumb: (
    message: string,
    category?: string,
    level?: string,
    data?: Record<string, unknown>
  ) => void;
  setUser: (user: { id?: string; email?: string; username?: string }) => void;
  setTag: (key: string, value: string) => void;
  setContext: (key: string, context: Record<string, unknown>) => void;
}

const SentryContext = createContext<SentryContextType | null>(null);

// Type for Sentry Replay integration with start/stop methods
interface ReplayIntegration {
  start?: () => void;
  stop?: () => void;
}

export const useSentry = () => {
  const context = useContext(SentryContext);
  if (!context) {
    throw new Error("useSentry must be used within a SentryProvider");
  }
  return context;
};

interface SentryProviderProps {
  children: ReactNode;
}

function SentryProviderContent({ children }: SentryProviderProps) {
  const [isClient, setIsClient] = useState(false);
  const [pathname, setPathname] = useState("");
  const [searchParams, setSearchParams] = useState<URLSearchParams>(
    () => new URLSearchParams(),
  );

  // Ensure we're on the client side
  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const syncLocation = () => {
      setPathname(window.location.pathname);
      setSearchParams(new URLSearchParams(window.location.search));
    };

    syncLocation();
    window.addEventListener("popstate", syncLocation);

    return () => {
      window.removeEventListener("popstate", syncLocation);
    };
  }, []);

  // Track page navigation - only on client side
  useEffect(() => {
    if (!isClient) return;

    const breadcrumbData = {
      pathname,
      searchParams: Object.fromEntries(searchParams.entries()),
      timestamp: new Date().toISOString(),
    };

    Sentry.addBreadcrumb({
      message: `Navigation to ${pathname}`,
      category: "navigation",
      level: "info",
      data: breadcrumbData,
    });

    // Set page context
    const isCandidatePage = pathname.includes("/candidates");
    Sentry.setContext("page", {
      pathname,
      searchParams: breadcrumbData.searchParams,
      isCandidatePage,
      isTokenPage: pathname.includes("/[token]"),
      token: pathname.includes("/candidates/")
        ? pathname.split("/candidates/")[1]
        : null,
    });

    // Control Sentry Replay based on route
    // Only record replays on candidates routes
    const replay = Sentry.getClient()?.getIntegrationByName?.("Replay") as
      | ReplayIntegration
      | undefined;
    if (replay) {
      if (isCandidatePage) {
        // Start/resume replay on candidates routes
        try {
          replay.start?.();
        } catch {
          // Replay might already be running
        }
      } else {
        // Stop replay on non-candidates routes
        try {
          replay.stop?.();
        } catch {
          // Replay might already be stopped
        }
      }
    }

    // Set performance context
    if (typeof window !== "undefined") {
      Sentry.setContext("performance", {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        screenResolution: `${screen.width}x${screen.height}`,
        colorDepth: screen.colorDepth,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      // Track memory usage if available
      if (
        (
          performance as Performance & {
            memory?: {
              usedJSHeapSize: number;
              totalJSHeapSize: number;
              jsHeapSizeLimit: number;
            };
          }
        ).memory
      ) {
        Sentry.setContext("memory", {
          usedJSHeapSize: (
            performance as Performance & {
              memory: {
                usedJSHeapSize: number;
                totalJSHeapSize: number;
                jsHeapSizeLimit: number;
              };
            }
          ).memory.usedJSHeapSize,
          totalJSHeapSize: (
            performance as Performance & {
              memory: {
                usedJSHeapSize: number;
                totalJSHeapSize: number;
                jsHeapSizeLimit: number;
              };
            }
          ).memory.totalJSHeapSize,
          jsHeapSizeLimit: (
            performance as Performance & {
              memory: {
                usedJSHeapSize: number;
                totalJSHeapSize: number;
                jsHeapSizeLimit: number;
              };
            }
          ).memory.jsHeapSizeLimit,
        });
      }
    }
  }, [isClient, pathname, searchParams]);

  // Enhanced DOM diagnostic function for DOM reconciliation errors
  const captureDetailedDOMDiagnostics = (error: Error) => {
    try {
      const active = document.activeElement as HTMLElement | null;

      // Capture ALL Radix Popovers with detailed info
      const popoverTriggers = Array.from(
        document.querySelectorAll("[data-radix-popover-trigger]")
      ).map((el) => {
        const trigger = el as HTMLElement;
        const isOpen = trigger.getAttribute("aria-expanded") === "true";
        const popoverId = trigger.getAttribute("aria-controls");
        const popoverContent = popoverId
          ? document.querySelector(`[id="${popoverId}"]`)
          : null;

        return {
          tagName: trigger.tagName,
          id: trigger.id,
          className: trigger.className,
          isOpen,
          popoverId,
          hasContent: !!popoverContent,
          contentClasses: popoverContent?.className || null,
          contentParent: popoverContent?.parentElement?.tagName || null,
          triggerHTML: trigger.outerHTML.slice(0, 300),
        };
      });

      // Capture ALL Radix Selects with detailed info
      const selectTriggers = Array.from(
        document.querySelectorAll("[data-radix-select-trigger]")
      ).map((el) => {
        const trigger = el as HTMLElement;
        const isOpen = trigger.getAttribute("aria-expanded") === "true";
        const selectId = trigger.getAttribute("aria-controls");
        const selectContent = selectId
          ? document.querySelector(`[id="${selectId}"]`)
          : null;

        return {
          tagName: trigger.tagName,
          id: trigger.id,
          className: trigger.className,
          isOpen,
          selectId,
          hasContent: !!selectContent,
          contentClasses: selectContent?.className || null,
          contentParent: selectContent?.parentElement?.tagName || null,
          triggerHTML: trigger.outerHTML.slice(0, 300),
        };
      });

      // Capture ALL portals (Radix and custom)
      const allPortals = Array.from(
        document.querySelectorAll(
          '[data-radix-portal], [data-radix-popper-content], [role="dialog"], [role="menu"], [role="listbox"]'
        )
      ).map((el) => {
        const portal = el as HTMLElement;
        return {
          tagName: portal.tagName,
          id: portal.id,
          className: portal.className,
          role: portal.getAttribute("role"),
          parentTag: portal.parentElement?.tagName || null,
          parentId: portal.parentElement?.id || null,
          parentClasses: portal.parentElement?.className || null,
          isVisible: portal.offsetParent !== null,
          zIndex: window.getComputedStyle(portal).zIndex,
          html: portal.outerHTML.slice(0, 500),
        };
      });

      // Capture body structure
      const bodyChildren = Array.from(document.body.children).map(
        (child, idx) => {
          const el = child as HTMLElement;
          return {
            index: idx,
            tagName: el.tagName,
            id: el.id,
            className: el.className,
            hasPortals: el.querySelectorAll(
              "[data-radix-portal], [data-radix-popper-content]"
            ).length,
            childCount: el.childElementCount,
          };
        }
      );

      // Try to extract React component info from error stack
      const reactComponentMatch = error.stack?.match(/at\s+(\w+)\s*\(/g) || [];
      const reactComponents = reactComponentMatch
        .map((match) => match.replace(/at\s+/, "").replace(/\(/, ""))
        .filter(
          (name) =>
            name &&
            !name.startsWith("a") &&
            !name.startsWith("_") &&
            name.length > 2 &&
            !/^[a-z]$/.test(name)
        )
        .slice(0, 10);

      // Capture recent breadcrumbs for context
      let recentBreadcrumbs: Sentry.Breadcrumb[] = [];
      try {
        const scope = Sentry.getCurrentScope();
        if (scope && "breadcrumbs" in scope) {
          const breadcrumbs = (scope as { breadcrumbs?: Sentry.Breadcrumb[] })
            .breadcrumbs;
          recentBreadcrumbs = Array.isArray(breadcrumbs)
            ? breadcrumbs.slice(-10)
            : [];
        }
      } catch {
        // Breadcrumbs may not be available
      }

      return {
        timestamp: new Date().toISOString(),
        activeElement: {
          tagName: active?.tagName || null,
          id: active?.id || null,
          className: active?.className || null,
          tag: active?.tagName || null,
        },
        popovers: {
          total: popoverTriggers.length,
          open: popoverTriggers.filter((p) => p.isOpen).length,
          details: popoverTriggers,
        },
        selects: {
          total: selectTriggers.length,
          open: selectTriggers.filter((s) => s.isOpen).length,
          details: selectTriggers,
        },
        portals: {
          total: allPortals.length,
          visible: allPortals.filter((p) => p.isVisible).length,
          details: allPortals,
        },
        bodyStructure: {
          childCount: document.body.childElementCount,
          children: bodyChildren,
        },
        reactStack: {
          components: reactComponents,
          fullStack: error.stack?.split("\n").slice(0, 30) || [],
        },
        recentBreadcrumbs: recentBreadcrumbs.map((b) => ({
          message: b.message,
          category: b.category,
          level: b.level,
          data: b.data,
          timestamp: b.timestamp,
        })),
        errorMessage: error.message,
        errorName: error.name,
      };
    } catch (diagError) {
      return {
        diagnosticError:
          diagError instanceof Error ? diagError.message : String(diagError),
      };
    }
  };

  // Capture uncaught errors at the window level to ensure nothing slips past
  useEffect(() => {
    if (typeof window === "undefined" || !isClient) return;

    const handleError = (event: ErrorEvent) => {
      try {
        const err =
          event.error instanceof Error
            ? event.error
            : new Error(String(event.message || "Unknown error"));
        const isDOMReconciliationError =
          err?.name === "NotFoundError" ||
          /insertBefore|removeChild|appendChild/.test(err?.message || "");

        // Enhanced DOM diagnostics for DOM reconciliation errors
        const domDiagnostics = isDOMReconciliationError
          ? captureDetailedDOMDiagnostics(err)
          : null;

        const domSnapshot = (() => {
          try {
            const active = document.activeElement as HTMLElement | null;
            const openPopovers = Array.from(
              document.querySelectorAll('[aria-expanded="true"]')
            ).map((el) => (el as HTMLElement).outerHTML?.slice(0, 200));
            const portals = Array.from(document.body.children)
              .filter((n) => n.nodeName === "DIV")
              .map((n) => (n as HTMLElement).className)
              .slice(0, 20);
            return {
              activeTag: active?.tagName,
              activeId: active?.id,
              activeClasses: active?.className,
              openTriggers: openPopovers,
              bodyChildCount: document.body.childElementCount,
              portalClassesSample: portals,
            };
          } catch {
            return undefined;
          }
        })();

        Sentry.addBreadcrumb({
          message: "window.error",
          category: "error",
          level: isDOMReconciliationError ? "warning" : "error",
          data: {
            message: err.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            pathname,
            isDOMReconciliationError,
            domSnapshot,
            ...(domDiagnostics
              ? {
                  openPopovers: domDiagnostics.popovers?.open ?? 0,
                  openSelects: domDiagnostics.selects?.open ?? 0,
                  portalsCount: domDiagnostics.portals?.total ?? 0,
                }
              : {}),
          },
        });
        Sentry.captureException(err, {
          tags: {
            source: "window.error",
            errorType: isDOMReconciliationError
              ? "dom_reconciliation"
              : undefined,
            // Add diagnostic tags for DOM errors
            ...(domDiagnostics
              ? {
                  openPopoversCount: (
                    domDiagnostics.popovers?.open ?? 0
                  ).toString(),
                  openSelectsCount: (
                    domDiagnostics.selects?.open ?? 0
                  ).toString(),
                  portalsCount: (domDiagnostics.portals?.total ?? 0).toString(),
                  visiblePortalsCount: (
                    domDiagnostics.portals?.visible ?? 0
                  ).toString(),
                }
              : {}),
          },
          extra: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            pathname,
            searchParams: Object.fromEntries(searchParams.entries()),
            userAgent: navigator.userAgent,
            domSnapshot,
            // CRITICAL: Include detailed diagnostics for DOM reconciliation errors
            ...(domDiagnostics
              ? {
                  domDiagnostics, // Full detailed diagnostics
                }
              : {}),
          },
          // Always use error level for exceptions to ensure they're sent to Sentry
          // The beforeSend hook will handle filtering if needed
          level: "error" as Sentry.SeverityLevel,
        });
      } catch {}
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      try {
        const reason = event.reason;

        // A DOM Event as the reason means a resource (<link>, <script>, <img>)
        // failed to load: network drop, ad blocker, aborted navigation. Not an
        // app bug, and it serialized as the useless `{"isTrusted":true}`
        // (ORQUESTI-NEXTJS-2DF). Keep it only as context for later errors.
        if (reason instanceof Event) {
          const target = reason.target as Element | null;
          Sentry.addBreadcrumb({
            message: "window.unhandledrejection (resource event)",
            category: "resource",
            level: "warning",
            data: {
              type: reason.type,
              targetTag: target?.tagName,
              targetSrc:
                target?.getAttribute?.("href") ?? target?.getAttribute?.("src"),
              pathname,
            },
          });
          return;
        }

        const err =
          reason instanceof Error
            ? reason
            : new Error(
                typeof reason === "string" ? reason : JSON.stringify(reason)
              );
        const isDOMReconciliationError =
          err?.name === "NotFoundError" ||
          /insertBefore|removeChild|appendChild/.test(err?.message || "");

        // Enhanced DOM diagnostics for DOM reconciliation errors
        const domDiagnostics = isDOMReconciliationError
          ? captureDetailedDOMDiagnostics(err)
          : null;

        const domSnapshot = (() => {
          try {
            const active = document.activeElement as HTMLElement | null;
            const openPopovers = Array.from(
              document.querySelectorAll('[aria-expanded="true"]')
            ).map((el) => (el as HTMLElement).outerHTML?.slice(0, 200));
            const portals = Array.from(document.body.children)
              .filter((n) => n.nodeName === "DIV")
              .map((n) => (n as HTMLElement).className)
              .slice(0, 20);
            return {
              activeTag: active?.tagName,
              activeId: active?.id,
              activeClasses: active?.className,
              openTriggers: openPopovers,
              bodyChildCount: document.body.childElementCount,
              portalClassesSample: portals,
            };
          } catch {
            return undefined;
          }
        })();

        Sentry.addBreadcrumb({
          message: "window.unhandledrejection",
          category: "error",
          level: isDOMReconciliationError ? "warning" : "error",
          data: {
            message: err.message,
            pathname,
            isDOMReconciliationError,
            domSnapshot,
            ...(domDiagnostics
              ? {
                  openPopovers: domDiagnostics.popovers?.open ?? 0,
                  openSelects: domDiagnostics.selects?.open ?? 0,
                  portalsCount: domDiagnostics.portals?.total ?? 0,
                }
              : {}),
          },
        });
        Sentry.captureException(err, {
          tags: {
            source: "window.unhandledrejection",
            errorType: isDOMReconciliationError
              ? "dom_reconciliation"
              : undefined,
            // Add diagnostic tags for DOM errors
            ...(domDiagnostics
              ? {
                  openPopoversCount: (
                    domDiagnostics.popovers?.open ?? 0
                  ).toString(),
                  openSelectsCount: (
                    domDiagnostics.selects?.open ?? 0
                  ).toString(),
                  portalsCount: (domDiagnostics.portals?.total ?? 0).toString(),
                  visiblePortalsCount: (
                    domDiagnostics.portals?.visible ?? 0
                  ).toString(),
                }
              : {}),
          },
          extra: {
            reason,
            pathname,
            searchParams: Object.fromEntries(searchParams.entries()),
            userAgent: navigator.userAgent,
            domSnapshot,
            // CRITICAL: Include detailed diagnostics for DOM reconciliation errors
            ...(domDiagnostics
              ? {
                  domDiagnostics, // Full detailed diagnostics
                }
              : {}),
          },
          // Always use error level for exceptions to ensure they're sent to Sentry
          // The beforeSend hook will handle filtering if needed
          level: "error" as Sentry.SeverityLevel,
        });
      } catch {}
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, [isClient, pathname, searchParams]);

  const captureError = (error: Error, context?: Record<string, unknown>) => {
    const errorContext = {
      ...context,
      timestamp: new Date().toISOString(),
      pathname,
      searchParams: Object.fromEntries(searchParams.entries()),
      userAgent: typeof window !== "undefined" ? navigator.userAgent : "server",
    };

    // Categorize error
    let errorCategory = "unknown";
    let errorSeverity = "error";
    const isDOMReconciliationError =
      error.name === "NotFoundError" ||
      /insertBefore|removeChild|appendChild/.test(error.message || "");

    if (error.message.includes("invariant expected app router to be mounted")) {
      errorCategory = "router_mount_error";
      errorSeverity = "error";
    } else if (error.message.includes("Network request failed")) {
      errorCategory = "network_error";
      errorSeverity = "warning";
    } else if (error.message.includes("ChunkLoadError")) {
      errorCategory = "chunk_load_error";
      errorSeverity = "error";
    } else if (error.message.includes("Loading chunk")) {
      errorCategory = "chunk_loading_error";
      errorSeverity = "error";
    } else if (error instanceof TypeError) {
      errorCategory = "type_error";
      errorSeverity = "error";
    } else if (error instanceof ReferenceError) {
      errorCategory = "reference_error";
      errorSeverity = "error";
    } else if (error.message.includes("Failed to fetch")) {
      errorCategory = "fetch_error";
      errorSeverity = "warning";
    } else if (isDOMReconciliationError) {
      errorCategory = "dom_reconciliation";
      errorSeverity = "warning";
    }

    // Enhanced DOM diagnostics for DOM reconciliation errors
    const domDiagnostics =
      isDOMReconciliationError && typeof window !== "undefined"
        ? captureDetailedDOMDiagnostics(error)
        : null;

    Sentry.captureException(error, {
      tags: {
        errorCategory,
        errorSeverity,
        component: (context?.component as string) || "unknown",
        page: pathname,
        isCandidatePage: pathname.includes("/candidates/"),
        hasToken: !!pathname.includes("/candidates/")
          ? pathname.split("/candidates/")[1]
          : null,
        // Add diagnostic tags for DOM errors
        ...(domDiagnostics
          ? {
              openPopoversCount: (
                domDiagnostics.popovers?.open ?? 0
              ).toString(),
              openSelectsCount: (domDiagnostics.selects?.open ?? 0).toString(),
              portalsCount: (domDiagnostics.portals?.total ?? 0).toString(),
              visiblePortalsCount: (
                domDiagnostics.portals?.visible ?? 0
              ).toString(),
            }
          : {}),
      },
      extra: {
        ...errorContext,
        // CRITICAL: Include detailed diagnostics for DOM reconciliation errors
        ...(domDiagnostics
          ? {
              domDiagnostics, // Full detailed diagnostics
            }
          : {}),
      },
      // Always use error level for exceptions to ensure they're sent to Sentry
      // The beforeSend hook will handle filtering if needed, but exceptions should always be errors
      level: "error" as "debug" | "info" | "warning" | "error" | "fatal",
      fingerprint: [errorCategory, pathname, error.message],
    });
  };

  const addBreadcrumb = (
    message: string,
    category: string = "user",
    level: string = "info",
    data?: Record<string, unknown>
  ) => {
    Sentry.addBreadcrumb({
      message,
      category,
      level: level as "debug" | "info" | "warning" | "error" | "fatal",
      data: {
        ...data,
        timestamp: new Date().toISOString(),
        pathname,
      },
    });
  };

  const setUser = (user: {
    id?: string;
    email?: string;
    username?: string;
  }) => {
    Sentry.setUser(user);
  };

  const setTag = (key: string, value: string) => {
    Sentry.setTag(key, value);
  };

  const setContext = (key: string, context: Record<string, unknown>) => {
    Sentry.setContext(key, context);
  };

  const value: SentryContextType = {
    captureError,
    addBreadcrumb,
    setUser,
    setTag,
    setContext,
  };

  return (
    <SentryContext.Provider value={value}>{children}</SentryContext.Provider>
  );
}

export function SentryProvider({ children }: SentryProviderProps) {
  return (
    <Suspense fallback={<div>{children}</div>}>
      <SentryProviderContent>{children}</SentryProviderContent>
    </Suspense>
  );
}
